"use client";
import { History } from "lucide-react";

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/app/admin/_common/ui/sheet";
import { formatUtcDate } from "@/app/_utils/dayjs";
import type { AutomationRun } from "@/app/_services/AutomationService";

import { RunStatusPill } from "./RunsTable";

function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="grid gap-1">
      <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span className="break-words text-[13px] text-foreground">{value || "-"}</span>
    </div>
  );
}

export default function RunDetailDrawer({
  run,
  onClose,
}: {
  run: AutomationRun | null;
  onClose: () => void;
}) {
  return (
    <Sheet open={run !== null} onOpenChange={(next) => !next && onClose()}>
      <SheetContent className="flex w-full flex-col gap-5 overflow-y-auto sm:max-w-[520px]">
        <SheetHeader>
          <SheetTitle>
            <span className="inline-flex items-center gap-2 align-middle">
              <History className="size-4 flex-none text-muted-foreground" />
              Run detail
            </span>
          </SheetTitle>
          <SheetDescription>
            {run ? formatUtcDate(run.created_at) : ""}
          </SheetDescription>
        </SheetHeader>

        {run && (
          <div className="flex flex-col gap-5">
            <div className="flex flex-wrap items-center gap-2">
              <RunStatusPill status={run.status} />
              <span className="text-[12px] text-muted-foreground">
                {run.mode} · {run.execution_path || "inline"}
              </span>
            </div>

            {(run.skip_reason || run.error) && (
              <div className="rounded-md border border-border bg-muted/40 px-3 py-2">
                <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  {run.error ? "Error" : "Why it was skipped"}
                </div>
                <div className="mt-1 break-words text-[12.5px] text-foreground">
                  {run.error || run.skip_reason}
                </div>
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Recipient" value={run.recipient} />
              <Field label="Event" value={run.event_key} />
              <Field label="Event ID" value={run.event_id} />
              <Field label="Dedupe key" value={run.dedupe_key} />
              <Field label="Action" value={run.action_type} />
              <Field
                label="Completed"
                value={run.completed_at ? formatUtcDate(run.completed_at) : null}
              />
            </div>

            {/* Delivery only appears once Postmark has told us, which needs the message
                id to have been captured on send. */}
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Provider message ID" value={run.provider_message_id} />
              <Field
                label="Delivered"
                value={run.delivered_at ? formatUtcDate(run.delivered_at) : null}
              />
              <Field
                label="Opened"
                value={run.opened_at ? formatUtcDate(run.opened_at) : null}
              />
            </div>

            {/* The point of shadow mode: what *would* have been sent, recorded without
                sending it. This is what gets compared against the legacy output. */}
            <div className="grid gap-2">
              <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                Rendered subject
              </span>
              <div className="rounded-md border border-border px-3 py-2 text-[13px] text-foreground">
                {run.rendered_subject || "-"}
              </div>
            </div>

            <div className="grid gap-2">
              <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                Rendered preview
              </span>
              <div className="max-h-[320px] overflow-auto rounded-md border border-border bg-muted/30 p-3">
                {run.rendered_preview ? (
                  // Rendered as escaped text, not injected as HTML: this is the raw
                  // body and the drawer is a debugging surface, not a preview pane.
                  <pre className="whitespace-pre-wrap break-words text-[11.5px] leading-relaxed text-foreground">
                    {run.rendered_preview}
                  </pre>
                ) : (
                  <span className="text-[12.5px] text-muted-foreground">
                    Nothing rendered
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
